/**
 * Feature Analytics Hook
 *
 * Provides automatic analytics tracking based on featureConfig.
 * Reads analytics configuration from featureConfig and provides a simple track function.
 *
 * @example
 * const track = useFeatureAnalytics(featureConfig);
 * track('create');
 * track('delete', { item: { id: 1, name: "Test" }, source: ' clicked from Dashboard Card' });
 */

import { useAnalytics } from './useAnalytics';

/**
 * Interpolates {variables} in event name string
 * @param {string} template - Event name template with {variable} placeholders
 * @param {Object} context - Key-value pairs for replacement
 * @returns {string} Interpolated event name
 *
 * @example
 * interpolate("filter by {value}", { value: "alphabetical" })
 * // Returns: "filter by alphabetical"
 */
const interpolate = (template, context = {}) => {
  if (!template || typeof template !== 'string') return '';
  return template.replace(/\{([\w.]+)\}/g, (match, path) => {
    // Support nested property access like {widget.title}
    const keys = path.split('.');
    let value = context;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return match;
    }
    return value ?? match;
  });
};

/**
 * Resolves payload (handles both static objects and functions)
 * @param {Object|Function} payloadConfig - Payload configuration
 * @param {Object} context - Context data to pass to payload function
 * @returns {Object} Resolved payload object
 *
 * @example
 * resolvePayload({ category: "test" }, null)
 * // Returns: { category: "test" }
 *
 * resolvePayload((ctx) => ({ id: ctx.item.id }), { item: { id: 123 } })
 * // Returns: { id: 123 }
 */
const resolvePayload = (payloadConfig, context = {}) => {
  if (typeof payloadConfig === 'function') {
    return payloadConfig(context);
  }
  return payloadConfig || {};
};

/**
 * Hook to track feature-specific analytics events
 * @param {Object} featureConfig - Feature configuration object with analytics section
 * @returns {Function} Track function
 *
 * @example
 * const track = useFeatureAnalytics(featureConfig);
 * track('create');
 * track('delete', { item: { id: 1, name: "Test" } });
 */
export const useFeatureAnalytics = (featureConfig) => {
  const analytics = useAnalytics();
  const analyticsConfig = featureConfig?.analytics || {};

  /**
   * Track an analytics event
   * @param {string} eventKey - Key from featureConfig.analytics
   * @param {Object} context - Context data for interpolation and payload
   */
  const track = (eventKey, context = {}) => {
    const eventConfig = analyticsConfig[eventKey];

    if (!eventConfig) {
      console.warn(`[Analytics] Event "${eventKey}" not configured in featureConfig.analytics`);
      return;
    }

    const { event, payload } = eventConfig;
    const eventName = interpolate(event, context);
    const eventPayload = resolvePayload(payload, context);

    // Call analytics service
    analytics.track(eventName, eventPayload);
  };

  return track;
};
